import React from 'react';
import { Button } from '@xingternal/button';
import { Headline, BodyCopy } from '@xingternal/typography';
import { IconArrowRight } from '@xingternal/icons';
import { useServerData } from '@xingternal/hops';
import { Link } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import cx from 'classnames';

import { useCompaniesIndustries } from '../../hooks/useCompaniesIndustries';
import {
  ROUTES,
  getIndustryRoute,
  getIndustrySearchPageRoute,
} from '../../routes/routes';
import { getIndustryName } from '@/utils/url';
import type { SetErrorType } from '@/types/common';
import { SkeletonLoggedOutIndustriesRow } from '@/components/Skeleton';
import styles from './LoggedOutLandingPage.css';
import { formatAmount } from '@/utils/formatAmount';

const LIMIT = 4;

const CompaniesIndustriesModule: React.FC<SetErrorType> = ({ setError }) => {
  const { data, loading, error } = useCompaniesIndustries({
    limit: LIMIT,
  });

  const { authenticated } = useServerData();
  const intl = useIntl();

  if (error) setError(!!error);
  if (loading) return <SkeletonLoggedOutIndustriesRow />;

  return (
    <section className={styles.companiesRow}>
      <Headline size="large" className={styles.companiesTitle}>
        <FormattedMessage id={'CP_100_DIRECTORY_TITLE_NEW'} />
      </Headline>
      <div className={styles.companiesContainer}>
        {data?.companiesIndustries?.collection?.map((item) => {
          const localizationValue = item?.industry?.localizationValue;
          const id = item?.industry?.id;
          const total = item?.total as number;
          const link = authenticated
            ? getIndustrySearchPageRoute({ id })
            : getIndustryRoute({ id, name: getIndustryName({ intl, id }) });
          return (
            <Link to={link} className={cx(styles.industry)} key={id}>
              <BodyCopy fontWeight="bold" size="small" noMargin>
                {localizationValue}
              </BodyCopy>
              <BodyCopy size="small" noMargin>
                <FormattedMessage
                  id={'CP_TOTAL_COMPANIES'}
                  values={{ total: formatAmount({ value: total, intl }) }}
                />
              </BodyCopy>
            </Link>
          );
        })}
        <div className={styles.industriesShowAll}>
          <Button
            to={ROUTES.industries}
            icon={IconArrowRight}
            iconAfter
            size="small"
            className={styles.industriesButton}
          >
            <FormattedMessage id="CP_SEE_ALL_INDUSTRIES" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export { CompaniesIndustriesModule };
